import { useState } from 'react';
import { Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomCellRendererProps } from 'ag-grid-react';
import { CellEditRequestEvent, ColDef, ColumnResizedEvent, ValueFormatterParams } from 'ag-grid-community';
import { InfniteScrollingGrid } from '../../AgGrid/InfiniteScrollingGrid';
import {
  getParcelSetting,
  searchParcelSettings,
  searchPropertyMappingsForParcels,
} from '../../../api/arcgis-civil3d-parcel-setting.api';
import { useSearchParcelStyles } from '../../../hooks/api/use-parcel-styles.hook';
import {
  useUpdateParcelSetting,
  useDeleteParcelSettings,
  useDuplicateParcelSettings,
  useCreatePropertyMappingsForParcels,
} from '../../../hooks/api/use-arcgis-civil3d-parcel-settings.hook';
import { Civil3dFeatureType } from '../../../../../types/constants/civil3d-feature-type';
import {
  ArcgisCivil3dParcelSettingApiModel,
  ArcgisCivil3dParcelSettingRelationship,
  WithParcelStyle,
} from '../../../../../types/models/arcgis-civil3d-parcel-setting.model';
import { ArcgisCivil3dConfigurationGrid } from '../ArcgisCivil3dConfigurationGrid';
import {
  WithArcgisFeatureClass,
  WithAutodeskLayer,
  WithConfiguration,
} from '../../../../../types/models/arcgis-civil3d-setting.model';

export const ParcelConfigurationGrid = () => {
  const navigate = useNavigate();
  const { configurationSettingId } = useParams<{ configurationSettingId: string }>();
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  const [updateParcelSetting, { error: updateError }] = useUpdateParcelSetting();
  const [deleteParcelSettings, { error: deleteError }] = useDeleteParcelSettings();
  const [duplicateParcelSettings, { error: duplicateError }] = useDuplicateParcelSettings();
  const [createPropertyMappings, { error: createPropertyMappingsError }] = useCreatePropertyMappingsForParcels();

  const { loading: loadingParcelStyles, response: parcelStylesResponse } = useSearchParcelStyles({
    limit: 999999,
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });

  const parcelStyleIdNameMap = parcelStylesResponse?.pages
    .flatMap(({ result }) => result)
    .reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {} as Record<string, string>);

  const columns: ColDef<
    ArcgisCivil3dParcelSettingApiModel &
      WithConfiguration &
      WithArcgisFeatureClass &
      WithAutodeskLayer &
      WithParcelStyle
  >[] = [
    {
      headerName: 'Style',
      field: 'parcelStyle',
      colId: 'parcelStyle.name',
      valueGetter: ({ data }) => data?.parcelStyle?.id,
      editable: true,
      sortable: true,
      flex: 0,
      width: columnWidths['parcelStyle.name'] ? Number(columnWidths['parcelStyle.name']) : 200,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: parcelStylesResponse?.pages.flatMap(({ result }) => result).map(({ id }) => id) ?? [],
        formatValue: (value: number) => parcelStyleIdNameMap?.[value] ?? '',
      },
      valueFormatter: (params: ValueFormatterParams<ArcgisCivil3dParcelSettingApiModel>) =>
        parcelStyleIdNameMap?.[params.value] ?? '',
    },
    {
      field: 'site',
      colId: 'site',
      flex: 0,
      width: columnWidths.site ? Number(columnWidths.site) : 200,
    },
    {
      headerName: 'Property Mappings',
      width: columnWidths.propertyMappings ? Number(columnWidths.propertyMappings) : 250,
      flex: 0,
      cellRenderer: ({ node }: CustomCellRendererProps<ArcgisCivil3dParcelSettingApiModel>) => (
        <Button variant="text" onClick={() => navigate(`${node.id}/property-mappings`)} sx={{ textTransform: 'none' }}>
          View property mapping
        </Button>
      ),
    },
  ];

  const onColumnResized = (params: ColumnResizedEvent) => {
    setColumnWidths((prevColumnWidths) =>
      params.column
        ? {
            ...(prevColumnWidths ?? {}),
            [params.column.getColId()]: params.column.getActualWidth(),
          }
        : prevColumnWidths,
    );
  };

  const getParcelSettings: Parameters<
    typeof InfniteScrollingGrid<
      ArcgisCivil3dParcelSettingApiModel &
        WithConfiguration &
        WithArcgisFeatureClass &
        WithAutodeskLayer &
        WithParcelStyle
    >
  >[0]['onGetRows'] = async ({ cursor, limit, filterQuery, sortQuery }) => {
    const { data } = await searchParcelSettings<
      ArcgisCivil3dParcelSettingApiModel &
        WithConfiguration &
        WithArcgisFeatureClass &
        WithAutodeskLayer &
        WithParcelStyle
    >({
      ...filterQuery,
      sort: sortQuery,
      cursor,
      limit,
      include: [
        ArcgisCivil3dParcelSettingRelationship.AUTODESK_LAYER,
        ArcgisCivil3dParcelSettingRelationship.ARCGIS_FEATURE_CLASS,
        ArcgisCivil3dParcelSettingRelationship.CONFIGURATION,
        ArcgisCivil3dParcelSettingRelationship.PARCEL_STYLE,
      ],
      configurationId: Number(configurationSettingId),
    });

    return data;
  };

  const updateSetting = async ({ colDef, data, newValue }: CellEditRequestEvent) => {
    if (!colDef?.field || !data?.id || colDef.field === 'query') return;

    const relationFieldMap = {
      autodeskLayer: 'autodeskLayerId',
      arcgisFeatureClass: 'arcgisFeatureClassId',
      parcelStyle: 'parcelStyleId',
    };

    const fieldKey =
      colDef.field in relationFieldMap ? relationFieldMap[colDef.field as keyof typeof relationFieldMap] : colDef.field;

    await updateParcelSetting({ parcelId: data.id, [fieldKey]: newValue });
  };

  const handleBulkDuplicatePropertyMappingsRecursive = async (
    originalDuplicateIdMap: Record<number, number>,
    idsToDuplicate: number[],
    cursor = 0,
  ) => {
    const { data } = await searchPropertyMappingsForParcels({
      parcelIds: idsToDuplicate,
      limit: 500,
      cursor,
    });

    const propertyMappingsToDuplicate = data.result.map(({ setting, attribute, civil3dProperty }) => ({
      parcelSettingId: originalDuplicateIdMap[setting!.id],
      attributeId: attribute.id,
      civil3dPropertyId: civil3dProperty.id,
    }));
    if (propertyMappingsToDuplicate.length > 0) {
      await createPropertyMappings({ propertyMappings: propertyMappingsToDuplicate });
    }

    if (data.nextCursor) {
      await handleBulkDuplicatePropertyMappingsRecursive(originalDuplicateIdMap, idsToDuplicate, data.nextCursor);
    }
  };

  return (
    <ArcgisCivil3dConfigurationGrid
      featureType={Civil3dFeatureType.PARCEL}
      error={updateError || deleteError || duplicateError || createPropertyMappingsError}
      loading={loadingParcelStyles}
      columns={columns}
      getSetting={async (parcelId) => {
        const { data } = await getParcelSetting({
          parcelId,
          include: [
            ArcgisCivil3dParcelSettingRelationship.CONFIGURATION,
            ArcgisCivil3dParcelSettingRelationship.ARCGIS_FEATURE_CLASS,
          ],
        });
        return data;
      }}
      onUpdateQuery={async (parcelId, query) => {
        await updateParcelSetting({ parcelId, query });
      }}
      onGetRows={getParcelSettings}
      onUpdateRow={updateSetting}
      onColumnResized={onColumnResized}
      onBulkDelete={async (settingIds) => {
        await deleteParcelSettings({ parcelIds: settingIds });
      }}
      onBulkDuplicate={async (settingIds) => {
        const duplicatedRecords = await duplicateParcelSettings({ parcelIds: settingIds });
        await handleBulkDuplicatePropertyMappingsRecursive(
          duplicatedRecords.reduce(
            (acc, { id, originalId }) => {
              if (!originalId) return acc;
              return { ...acc, [originalId]: id };
            },
            {} as Record<number, number>,
          ),
          settingIds,
        );
      }}
    />
  );
};
